import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useAstrologerProfile } from '../../hooks/useAstrologerProfile';

const STEPS = ['Quem é você', 'Como te encontram', 'Seu documento'] as const;

export function ProfessionalActivationWizard() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { astrologerProfile, refresh } = useAstrologerProfile();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 0, Identity
  const [professionalName, setProfessionalName] = useState(profile?.full_name ?? '');
  const [title, setTitle]             = useState('');
  const [bio, setBio]                 = useState('');
  const [specialties, setSpecialties] = useState('');
  const [yearsExp, setYearsExp]       = useState('');

  // Step 1, Contact
  const [contactEmail, setContactEmail] = useState(profile?.email ?? '');
  const [whatsapp, setWhatsapp]         = useState('');
  const [website, setWebsite]           = useState('');
  const [instagram, setInstagram]       = useState('');
  const [showEmail, setShowEmail]       = useState(true);
  const [showWhatsapp, setShowWhatsapp] = useState(true);

  // Step 2, Document
  const [headerType, setHeaderType]       = useState<'name_only' | 'name_and_contact' | 'full_header' | 'custom_message' | 'logo_only' | 'logo_and_contact'>('name_only');
  const [primaryColor, setPrimaryColor]   = useState('#0A1628');
  const [accentColor, setAccentColor]     = useState('#C49A22');
  const [footerText, setFooterText]       = useState('');
  const [includeDisclaimer, setDisclaimer]= useState(true);

  async function handleFinish() {
    if (!user) return;
    setSaving(true);
    setError(null);
    try {
      const specList = specialties.split(',').map(s => s.trim()).filter(Boolean);
      const payload = {
        user_id:           user.id,
        professional_name: professionalName.trim(),
        title:             title.trim() || null,
        bio:               bio.trim() || null,
        specialties:       specList.length ? specList : null,
        years_experience:  parseInt(yearsExp) || null,
        contact_email:     contactEmail.trim() || null,
        whatsapp:          whatsapp.trim() || null,
        website:           website.trim() || null,
        instagram:         instagram.trim() || null,
        show_email:        showEmail,
        show_whatsapp:     showWhatsapp,
        doc_header_type:   headerType,
        doc_primary_color: primaryColor,
        doc_accent_color:  accentColor,
        doc_footer_text:   footerText.trim() || null,
        doc_include_disclaimer: includeDisclaimer,
        wizard_completed:  true,
        is_active:         true,
      };

      if (astrologerProfile) {
        const { error: e } = await supabase.from('astrologer_profiles').update(payload).eq('id', astrologerProfile.id);
        if (e) throw new Error(e.message);
      } else {
        const { error: e } = await supabase.from('astrologer_profiles').insert(payload);
        if (e) throw new Error(e.message);
      }

      await refresh();
      navigate('/profissional/dashboard');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function nextStep() {
    if (step === 0 && !professionalName.trim()) { setError('Nome profissional é obrigatório.'); return; }
    setError(null);
    if (step < 2) setStep(s => s + 1);
    else handleFinish();
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-base)', padding: '2rem' }}>
      <div style={{ width: '100%', maxWidth: '580px' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <p style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: 'var(--petrol)', margin: '0 0 0.5rem' }}>✦ Professya Pro</p>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)', margin: 0 }}>
            Bem-vinda! Vamos configurar seu perfil profissional em 3 passos.
          </p>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', justifyContent: 'center' }}>
          {STEPS.map((s, i) => (
            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <div style={{
                width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: i <= step ? 'var(--petrol)' : 'var(--bg-elevated)',
                color: i <= step ? '#000' : 'var(--text-muted)', fontSize: '11px', fontWeight: 700,
              }}>
                {i + 1}
              </div>
              <span style={{ fontSize: 'var(--text-caption)', color: i === step ? 'var(--text-primary)' : 'var(--text-muted)' }}>{s}</span>
              {i < 2 && <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>›</span>}
            </div>
          ))}
        </div>

        <div className="card">
          {/* Step 0: Identity */}
          {step === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)', margin: 0 }}>Quem é você profissionalmente?</p>
              <div>
                <label className="form-label">Nome profissional *</label>
                <input className="form-input" value={professionalName} onChange={e => setProfessionalName(e.target.value)} placeholder="Ana Carolina Estrela" />
              </div>
              <div>
                <label className="form-label">Título</label>
                <input className="form-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Astróloga Profissional" />
              </div>
              <div>
                <label className="form-label">Bio ({bio.length}/500)</label>
                <textarea className="form-input" value={bio} onChange={e => setBio(e.target.value.slice(0, 500))} style={{ minHeight: '80px', resize: 'vertical', width: '100%' }} placeholder="Conte um pouco sobre você e sua prática..." />
              </div>
              <div>
                <label className="form-label">Especialidades (separadas por vírgula)</label>
                <input className="form-input" value={specialties} onChange={e => setSpecialties(e.target.value)} placeholder="Karmica, Vocacional, Solar" />
              </div>
              <div>
                <label className="form-label">Anos de experiência</label>
                <input className="form-input" type="number" min={0} value={yearsExp} onChange={e => setYearsExp(e.target.value)} />
              </div>
            </div>
          )}

          {/* Step 1: Contact */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)', margin: 0 }}>Como seus clientes podem te encontrar?</p>
              {[
                { label: 'E-mail de contato', value: contactEmail, set: setContactEmail, show: showEmail, setShow: setShowEmail, type: 'email' },
                { label: 'WhatsApp', value: whatsapp, set: setWhatsapp, show: showWhatsapp, setShow: setShowWhatsapp, type: 'tel' },
              ].map(({ label, value, set: s, show, setShow, type }) => (
                <div key={label}>
                  <label className="form-label">{label}</label>
                  <input className="form-input" type={type} value={value} onChange={e => s(e.target.value)} />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.3rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={show} onChange={e => setShow(e.target.checked)} style={{ accentColor: 'var(--petrol)' }} />
                    Exibir no perfil público
                  </label>
                </div>
              ))}
              <div>
                <label className="form-label">Website (opcional)</label>
                <input className="form-input" type="url" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://..." />
              </div>
              <div>
                <label className="form-label">Instagram (opcional)</label>
                <input className="form-input" value={instagram} onChange={e => setInstagram(e.target.value)} placeholder="@sua.conta" />
              </div>
            </div>
          )}

          {/* Step 2: Document */}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)', margin: 0 }}>Como ficará seu documento Word?</p>

              <div>
                <label className="form-label">Tipo de cabeçalho</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {[
                    { v: 'name_only',        l: 'Só nome e título' },
                    { v: 'name_and_contact', l: 'Nome + contatos em linha' },
                    { v: 'full_header',      l: 'Cabeçalho completo' },
                    { v: 'custom_message',   l: 'Nome + mensagem personalizada' },
                    { v: 'logo_only',        l: 'Apenas logo' },
                    { v: 'logo_and_contact', l: 'Logo + contatos' },
                  ].map(({ v, l }) => (
                    <label key={v} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: 'var(--text-label)', color: headerType === v ? 'var(--petrol)' : 'var(--text-secondary)' }}>
                      <input type="radio" name="ht" value={v} checked={headerType === v} onChange={() => setHeaderType(v as typeof headerType)} style={{ accentColor: 'var(--petrol)' }} />
                      {l}
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label className="form-label">Cor primária</label>
                  <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} style={{ width: '100%', height: '36px', borderRadius: '6px', border: '1px solid var(--border-subtle)', cursor: 'pointer' }} />
                </div>
                <div>
                  <label className="form-label">Cor de destaque</label>
                  <input type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)} style={{ width: '100%', height: '36px', borderRadius: '6px', border: '1px solid var(--border-subtle)', cursor: 'pointer' }} />
                </div>
              </div>

              <div>
                <label className="form-label">Texto do rodapé</label>
                <input className="form-input" value={footerText} onChange={e => setFooterText(e.target.value)} placeholder="Seu Nome · seusite.com" />
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
                <input type="checkbox" checked={includeDisclaimer} onChange={e => setDisclaimer(e.target.checked)} style={{ accentColor: 'var(--petrol)' }} />
                Incluir disclaimer de responsabilidade no documento
              </label>
            </div>
          )}

          {error && <p style={{ color: '#f87171', fontSize: 'var(--text-caption)', margin: '0.75rem 0 0' }}>{error}</p>}
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
          {step > 0 && (
            <button className="btn-secondary" onClick={() => setStep(s => s - 1)} disabled={saving}>
              ← Anterior
            </button>
          )}
          <button className="btn-primary" onClick={nextStep} disabled={saving} style={{ flex: 1 }}>
            {saving ? 'Salvando...' : step < 2 ? 'Próximo →' : 'Finalizar configuração ✦'}
          </button>
        </div>
      </div>
    </div>
  );
}
